<!-- The note's tags (Space tags first, then the user's private ones 🔒), each with ×, then "Assign tag…" — as in Edit. -->
<div class="flex min-w-0 flex-wrap items-center gap-1.5" role="list" aria-label="Tags" data-testid="note-tag-editor">
  @for (tag of noteTags; track tag._id) {
    <span
      role="listitem"
      class="note-tag-chip inline-flex h-7 items-center overflow-hidden rounded-full border border-gray-300 bg-white text-xs font-medium text-(--p-text-color) dark:border-slate-600 dark:bg-slate-800"
      [style.opacity]="tag.isActive === false ? 0.6 : null">
      <span class="inline-flex items-center gap-1.5 pl-2.5 pr-1">
        <span class="h-2 w-2 flex-shrink-0 rounded-full border border-black/10" [style.backgroundColor]="tag.color || '#94a3b8'" aria-hidden="true"></span>
        {{ tag.name }}</span>
      <button
        type="button"
        class="inline-flex h-full w-6 cursor-pointer items-center justify-center border-0 bg-transparent text-inherit opacity-80 hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
        [attr.aria-label]="'Remove tag ' + tag.name"
        [title]="'Remove tag ' + tag.name"
        data-testid="remove-tag"
        (click)="removeSpaceTag(tag)">
        <span class="material-symbols-outlined !text-base" aria-hidden="true">close</span>
      </button>
    </span>
  }
  @for (tag of privateNoteTags; track tag._id) {
    <span
      role="listitem"
      class="note-tag-chip inline-flex h-7 items-center overflow-hidden rounded-full border border-gray-300 bg-white text-xs font-medium text-(--p-text-color) dark:border-slate-600 dark:bg-slate-800"
      [style.opacity]="tag.isActive === false ? 0.6 : null">
      <span class="inline-flex items-center gap-1 pl-2 pr-1" [title]="'Private tag (only you can see it)'">
        <span class="h-2 w-2 flex-shrink-0 rounded-full border border-black/10" [style.backgroundColor]="tag.color || '#94a3b8'" aria-hidden="true"></span>
        <i class="pi pi-lock !text-[0.625rem] opacity-70" aria-hidden="true"></i>
        <span class="sr-only">Private tag:</span>{{ tag.name }}</span>
      <button
        type="button"
        class="inline-flex h-full w-6 cursor-pointer items-center justify-center border-0 bg-transparent text-inherit opacity-80 hover:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary"
        [attr.aria-label]="'Remove private tag ' + tag.name"
        [title]="'Remove private tag ' + tag.name"
        data-testid="remove-tag"
        (click)="removePrivateTag(tag)">
        <span class="material-symbols-outlined !text-base" aria-hidden="true">close</span>
      </button>
    </span>
  }

  <p-autocomplete
    #quickTagAutocomplete
    class="inline-flex w-40 max-w-full"
    inputStyleClass="!h-8 !w-full !min-w-0 !rounded-full !px-3 !text-sm"
    [(ngModel)]="quickTagName"
    [suggestions]="tagSuggestionGroups"
    [group]="true"
    optionGroupLabel="label"
    optionGroupChildren="items"
    [dropdown]="true"
    panelStyleClass="min-w-64"
    scrollHeight="22rem"
    [appendTo]="'body'"
    optionLabel="name"
    placeholder="Assign tag…"
    ariaLabel="Assign tag"
    dropdownAriaLabel="Show all tags"
    (onFocus)="showAvailableTagSuggestions()"
    (completeMethod)="searchTagSuggestions($event)"
    (onSelect)="onTagAutocompleteSelected($event)"
    (keydown.enter)="$event.preventDefault(); addQuickTag()">
    <ng-template let-group pTemplate="group">
      <div class="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-(--p-text-muted-color)">
        @if (group.scope === TAG_SCOPE.PRIVATE) {
          <i class="pi pi-lock !text-[0.625rem]" aria-hidden="true"></i>
        }
        {{ group.label }}
        @if (group.scope === TAG_SCOPE.PRIVATE && !canChangePrivateTags) {
          <span class="font-normal normal-case tracking-normal">· {{ privateTagsBlockedReason }}</span>
        }
      </div>
    </ng-template>
    <ng-template let-tag pTemplate="item">
      <div class="flex items-center gap-2">
        <span class="h-3 w-3 shrink-0 rounded-full border border-black/10" [style.backgroundColor]="tag.color || '#94a3b8'"></span>
        <div class="min-w-0 flex-1">
          <div class="flex items-center gap-1 truncate text-sm font-medium">
            @if (tag.scope !== TAG_SCOPE.SPACE) {
              <i class="pi pi-lock !text-[0.625rem] opacity-70" aria-label="Private tag"></i>
            }
            {{ tag.name }}
          </div>
          <div class="truncate text-xs text-(--p-text-muted-color)">
            {{ tag.scope === TAG_SCOPE.SPACE ? 'Space' : 'Private · only you' }} · {{ tag.tagType }}
          </div>
        </div>
      </div>
    </ng-template>
  </p-autocomplete>

  @if (quickTagInputText.trim()) {
    <!-- New-tag scope: a choice wherever a Space tag can be created (SHARED Space, write access); else private. -->
    @if (canCreateSpaceTags) {
      <p-selectbutton
        [options]="tagScopeOptions"
        [(ngModel)]="newTagScope"
        [allowEmpty]="false"
        optionLabel="label"
        optionValue="value"
        size="small"
        ariaLabelledBy="noteTagEditorScopeLabel">
        <ng-template #item let-option>
          <span class="inline-flex items-center gap-1 text-xs" [title]="option.value === TAG_SCOPE.PRIVATE ? 'Only you can see private tags' : 'Visible to everyone in this Space'">
            <i [class]="option.icon + ' !text-[0.7rem]'" aria-hidden="true"></i>{{ option.label }}
          </span>
        </ng-template>
      </p-selectbutton>
      <span id="noteTagEditorScopeLabel" class="sr-only">Create new tag as</span>
    }
    <button
      type="button"
      class="inline-flex h-8 cursor-pointer items-center gap-1 rounded-full border-0 bg-primary px-3 text-xs font-medium text-primary-contrast disabled:cursor-not-allowed disabled:opacity-50"
      [disabled]="isTagMutating"
      [title]="effectiveNewTagScope === TAG_SCOPE.PRIVATE ? 'Private tag — only you can see it' : 'Space tag — visible to everyone in this Space'"
      (click)="addQuickTag()">
      <span class="material-symbols-outlined !text-base" aria-hidden="true">add</span>
      @if (effectiveNewTagScope === TAG_SCOPE.PRIVATE) {
        <i class="pi pi-lock !text-[0.625rem]" aria-hidden="true"></i>
      }
      {{ quickTagExistsInScope ? 'Add' : 'Create' }} “{{ quickTagInputText.trim() }}”
    </button>
  }
</div>
